import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import {
  AccessTime as TimeIcon,
  Payment as PaymentIcon,
  ArrowBack as BackIcon,
  Videocam as VideocamIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { Service, Slot, servicesApi, slotsApi, bookingApi } from '../services/api';

interface PaymentInitResponse {
  booking_details: {
    service_name: string;
    date: string;
    time: string;
  };
  payment_details: {
    order_id: string;
    amount: number;
    currency: string;
    key_id: string;
  };
}

const BookSlot: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const selectedServiceFromState: Service | null = location.state?.selectedService || null;

  const [services, setServices] = useState<Service[]>([]);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    selectedService: selectedServiceFromState as Service | null,
    selectedSlot: null as Slot | null,
    consultationMode: 'offline' as 'online' | 'offline',
  });

  const steps = ['Choose Date & Time', 'Your Details', 'Confirm & Pay'];

  // Only fetch services if none was passed from Services page
  useEffect(() => {
    if (!selectedServiceFromState) {
      void fetchServices();
    }
  }, []);

  useEffect(() => {
    if (selectedDate) void fetchSlots(selectedDate.format('YYYY-MM-DD'));
  }, [selectedDate]);

  const fetchServices = async () => {
    try {
      setLoading(true);
      const data = await servicesApi.getAll();
      setServices(data);
    } catch {
      setError('Failed to load services');
    } finally { setLoading(false); }
  };

  const fetchSlots = async (date: string) => {
    try {
      setLoading(true);
      const data = await slotsApi.getByDate(date);
      setSlots(data);
    } catch {
      setError('Failed to load slots for selected date');
    } finally { setLoading(false); }
  };

  const handleSlotSelect = (slot: Slot) => {
    setBookingForm(prev => ({ ...prev, selectedSlot: slot }));
    setActiveStep(1);
  };

  const handleDetailsSubmit = () => {
    if (!bookingForm.name || !bookingForm.email || !bookingForm.phone) return;
    setActiveStep(2);
  };

  const handleConfirmAndPay = async () => {
    if (!bookingForm.selectedService || !bookingForm.selectedSlot) return;
    try {
      setLoading(true);
      const response: PaymentInitResponse = await bookingApi.bookSlot({
        slot_id: bookingForm.selectedSlot.id,
        user_details: { name: bookingForm.name, email: bookingForm.email, phone: bookingForm.phone },
        service_id: bookingForm.selectedService.id,
        consultation_mode: bookingForm.consultationMode,
      });

      // Open Razorpay directly
      const options = {
        key: response.payment_details.key_id,
        amount: response.payment_details.amount,
        currency: response.payment_details.currency,
        name: 'Amrutha Kalasam',
        description: `${response.booking_details.service_name} - ${response.booking_details.date} ${response.booking_details.time}`,
        order_id: response.payment_details.order_id,
        handler: async (rzpResponse: any) => {
          try {
            await bookingApi.verifyPayment({
              razorpay_order_id: rzpResponse.razorpay_order_id,
              razorpay_payment_id: rzpResponse.razorpay_payment_id,
              razorpay_signature: rzpResponse.razorpay_signature,
            });
            setSuccessMessage(
              bookingForm.consultationMode === 'online'
                ? '🎉 Payment successful! Your online consultation is confirmed. Check your email for the video call link.'
                : '🎉 Payment successful! Your in-person consultation is confirmed. Check your email for location details and contact info.'
            );
            resetForm();
          } catch {
            setError('Payment verification failed. Please contact support.');
          }
        },
        prefill: { name: bookingForm.name, email: bookingForm.email, contact: bookingForm.phone },
        theme: { color: '#7B2D26' },
        modal: {
          ondismiss: async () => {
            try {
              if (response.payment_details.order_id) {
                await bookingApi.cancelPayment({ razorpay_order_id: response.payment_details.order_id });
              }
            } catch (e) {
              console.error('Cancel payment failed', e);
            }
            setError('Payment was cancelled. Your slot has been released.');
            if (selectedDate) void fetchSlots(selectedDate.format('YYYY-MM-DD'));
          }
        },
      } as any;
      const razorpay = new (window as any).Razorpay(options);
      razorpay.open();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to initiate booking. Please try again.');
    } finally { setLoading(false); }
  };

  const resetForm = () => {
    setBookingForm({ name: '', email: '', phone: '', selectedService: selectedServiceFromState, selectedSlot: null, consultationMode: 'offline' });
    setActiveStep(0);
    if (selectedDate) void fetchSlots(selectedDate.format('YYYY-MM-DD'));
  };

  const formatPrice = (price: number) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(price);
  const formatTime = (time: string) => dayjs(`2000-01-01 ${time}`).format('hh:mm A');

  const availableSlots = slots.filter(slot => slot.date === selectedDate?.format('YYYY-MM-DD') && !slot.is_booked);

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: { xs: 2, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ textAlign: 'center', mb: { xs: 3, md: 4 }, pt: { xs: 2, md: 3 } }}>
        <Typography variant="h1" sx={{ mb: 1 }}>
          Book Your Consultation
        </Typography>
        {bookingForm.selectedService ? (
          <Chip
            label={`${bookingForm.selectedService.name} • ${formatPrice(bookingForm.selectedService.price)}`}
            sx={{ mt: 1, fontSize: '0.9rem', py: 2.5, px: 1, backgroundColor: '#FFF5EB', color: 'primary.main', fontWeight: 600 }}
          />
        ) : (
          <Typography variant="body1" sx={{ color: 'text.secondary' }}>
            Choose your service and pick a convenient time
          </Typography>
        )}
      </Box>

      {/* Stepper */}
      <Card sx={{ mb: 3, p: { xs: 2, md: 3 } }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>
      </Card>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Service selector — only when no service was pre-selected */}
      {!bookingForm.selectedService && (
        <Card sx={{ p: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>First, select a service</Typography>
            <FormControl fullWidth>
              <InputLabel>Choose Service</InputLabel>
              <Select
                value=""
                label="Choose Service"
                onChange={(e) => {
                  const svc = services.find(s => s.id === e.target.value);
                  if (svc) setBookingForm(prev => ({ ...prev, selectedService: svc }));
                }}
              >
                {services.map(s => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} — {formatPrice(s.price)} ({s.duration_minutes} min)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="text" onClick={() => navigate('/services')} sx={{ mt: 2 }}>
              ← View all services with details
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Step 1: Date & Time */}
      {activeStep === 0 && bookingForm.selectedService && (
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
              Choose Date & Time
            </Typography>

            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
              <DatePicker
                label="Select Date"
                value={selectedDate}
                onChange={setSelectedDate}
                minDate={dayjs()}
                maxDate={dayjs().add(210, 'day')}
              />
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : selectedDate && (
              <Box>
                <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'text.secondary' }}>
                  {selectedDate.format('dddd, MMMM DD, YYYY')}
                </Typography>

                {availableSlots.length > 0 ? (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    {availableSlots.map(slot => (
                      <Button
                        key={slot.id}
                        variant={bookingForm.selectedSlot?.id === slot.id ? 'contained' : 'outlined'}
                        onClick={() => handleSlotSelect(slot)}
                        startIcon={<TimeIcon />}
                        sx={{
                          p: 2,
                          fontSize: '1rem',
                          justifyContent: 'flex-start',
                          textTransform: 'none',
                        }}
                      >
                        {formatTime(slot.start_time)} — {formatTime(slot.end_time)}
                      </Button>
                    ))}
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ textAlign: 'center' }}>
                    No slots available for this date. Try another date.
                  </Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 2: Your Details */}
      {activeStep === 1 && bookingForm.selectedService && bookingForm.selectedSlot && (
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
              Your Details
            </Typography>

            <Box sx={{ maxWidth: 480, mx: 'auto' }}>
              {/* Consultation Mode Toggle */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>
                  Consultation Type
                </Typography>
                <ToggleButtonGroup
                  value={bookingForm.consultationMode}
                  exclusive
                  onChange={(_, val) => val && setBookingForm(prev => ({ ...prev, consultationMode: val }))}
                  sx={{
                    '& .MuiToggleButton-root': {
                      px: 3, py: 1.5, textTransform: 'none', fontSize: '0.95rem',
                      '&.Mui-selected': { backgroundColor: 'primary.main', color: '#fff', '&:hover': { backgroundColor: 'primary.dark' } },
                    },
                  }}
                >
                  <ToggleButton value="offline">
                    <LocationIcon sx={{ mr: 1 }} /> In-Person
                  </ToggleButton>
                  <ToggleButton value="online">
                    <VideocamIcon sx={{ mr: 1 }} /> Online Video
                  </ToggleButton>
                </ToggleButtonGroup>

                {bookingForm.consultationMode === 'online' ? (
                  <Typography variant="body2" sx={{ mt: 1, color: 'info.main', fontSize: '0.8rem' }}>
                    🎥 You'll receive a Jitsi video call link via email after payment
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                    📍 Location, Google Maps link & WhatsApp contact will be emailed after payment
                  </Typography>
                )}
              </Box>

              <TextField
                fullWidth
                label="Full Name"
                value={bookingForm.name}
                onChange={(e) => setBookingForm(prev => ({ ...prev, name: e.target.value }))}
                sx={{ mb: 2.5 }}
                required
              />
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={bookingForm.email}
                onChange={(e) => setBookingForm(prev => ({ ...prev, email: e.target.value }))}
                sx={{ mb: 2.5 }}
                required
              />
              <TextField
                fullWidth
                label="Phone Number"
                value={bookingForm.phone}
                onChange={(e) => setBookingForm(prev => ({ ...prev, phone: e.target.value }))}
                sx={{ mb: 3 }}
                required
                placeholder="+91 XXXXX XXXXX"
              />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<BackIcon />}
                  onClick={() => setActiveStep(0)}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleDetailsSubmit}
                  disabled={!bookingForm.name || !bookingForm.email || !bookingForm.phone}
                  sx={{ px: 4 }}
                >
                  Review Booking
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Confirm & Pay */}
      {activeStep === 2 && bookingForm.selectedService && bookingForm.selectedSlot && (
        <Card sx={{ p: { xs: 2, md: 3 }, mb: 3 }}>
          <CardContent>
            <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
              Confirm & Pay
            </Typography>

            {/* Booking Summary */}
            <Card sx={{ p: 3, mb: 3, backgroundColor: '#FFF5EB', border: '1px solid #F0E6DA' }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>Service</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{bookingForm.selectedService.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>Date & Time</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {selectedDate?.format('MMM DD, YYYY')} • {formatTime(bookingForm.selectedSlot.start_time)} - {formatTime(bookingForm.selectedSlot.end_time)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>Name</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{bookingForm.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>Contact</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{bookingForm.email}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{bookingForm.phone}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 1 }}>Consultation Mode</Typography>
                  <Chip
                    icon={bookingForm.consultationMode === 'online' ? <VideocamIcon /> : <LocationIcon />}
                    label={bookingForm.consultationMode === 'online' ? 'Online Video Call' : 'In-Person Visit'}
                    size="small"
                    sx={{ mt: 0.5, fontWeight: 600, backgroundColor: bookingForm.consultationMode === 'online' ? '#e3f2fd' : '#FFF5EB' }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">Total Amount</Typography>
                <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 700 }}>
                  {formatPrice(bookingForm.selectedService.price)}
                </Typography>
              </Box>
            </Card>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                startIcon={<BackIcon />}
                onClick={() => setActiveStep(1)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                size="large"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PaymentIcon />}
                onClick={handleConfirmAndPay}
                disabled={loading}
                sx={{ px: 5, py: 1.5, fontSize: '1.05rem' }}
              >
                {loading ? 'Processing...' : 'Pay Now'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default BookSlot;
